import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, Sun, Moon, ChevronDown, LogIn, LogOut, UserRound } from "lucide-react";
import { useUiStore, type Language } from "@/stores/ui.store";
import { Avatar } from "@/ui/Avatar";
import { useCurrentUser, useLogout } from "@/features/auth/auth.hooks";
import { useUnreadNotificationsCount } from "@/features/notifications/notifications.hooks";
import { cn } from "@/lib/cn";
import { GlobalSearch } from "./GlobalSearch";

const LANGS: { code: Language; flag: string; label: string }[] = [
  { code: "fr", flag: "🇫🇷", label: "FR" },
  { code: "en", flag: "🇬🇧", label: "EN" },
  { code: "ar", flag: "🇩🇿", label: "AR" },
];

export function Topbar() {
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const language = useUiStore((s) => s.language);
  const setLanguage = useUiStore((s) => s.setLanguage);
  const setNotificationsOpen = useUiStore((s) => s.setNotificationsOpen);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const logout = useLogout();
  const unreadCount = useUnreadNotificationsCount();
  const [langOpen, setLangOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);

  const isDark = theme !== "light";
  const current = LANGS.find((l) => l.code === language) ?? LANGS[0]!;

  return (
    <header className="flex items-center gap-3 px-5 h-[var(--topbar-h)] min-h-[var(--topbar-h)] bg-bg/95 border-b border-border relative z-50">
      <GlobalSearch />

      <div className="ml-auto flex items-center gap-1.5">
        <button
          onClick={() => setNotificationsOpen(true)}
          className="relative flex items-center justify-center h-9 w-9 rounded-sm text-text2 hover:bg-bg4 hover:text-text transition-colors"
          aria-label={t("topbar.notifications")}
        >
          <Bell className="h-[15px] w-[15px]" />
          {unreadCount > 0 && (
            <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-danger ring-2 ring-bg" />
          )}
        </button>

        <button
          onClick={() => setTheme(isDark ? "light" : "dark")}
          className="flex items-center justify-center h-9 w-9 rounded-sm text-text2 hover:bg-bg4 hover:text-text transition-colors"
          aria-label={t("topbar.theme")}
        >
          {isDark ? <Sun className="h-[15px] w-[15px]" /> : <Moon className="h-[15px] w-[15px]" />}
        </button>

        <div className="w-px h-[18px] bg-border mx-1" />

        <div className="relative">
          <button
            onClick={() => setLangOpen((v) => !v)}
            onBlur={() => setTimeout(() => setLangOpen(false), 120)}
            className="flex items-center gap-1.5 h-9 px-2.5 rounded-sm bg-bg3 border border-border text-[11px] font-semibold text-text2 hover:border-primary hover:text-primary transition-all"
          >
            <span>{current.flag}</span>
            {current.label}
            <ChevronDown className="h-2.5 w-2.5" />
          </button>
          {langOpen && (
            <div className="absolute top-[calc(100%+6px)] right-0 min-w-[140px] bg-bg3 border border-border2 rounded p-1 z-[1000] shadow-card animate-fadeIn">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => {
                    setLanguage(l.code);
                    setLangOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 px-3 py-2 rounded-sm text-[12.5px] hover:bg-white/5 transition-colors",
                    l.code === language ? "text-primary" : "text-text",
                  )}
                >
                  <span>{l.flag}</span>
                  {l.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="w-px h-[18px] bg-border mx-1" />

        {user ? (
          <div className="relative">
            <button
              onClick={() => setUserOpen((v) => !v)}
              onBlur={() => setTimeout(() => setUserOpen(false), 120)}
              className="flex items-center gap-2 h-9 pl-1 pr-2 rounded-sm hover:bg-bg4 transition-colors"
            >
              <Avatar name={user.name} size={24} />
              <span className="text-[11px] font-semibold text-text2 max-w-[110px] truncate">{user.name}</span>
              <ChevronDown className="h-2.5 w-2.5 text-text3" />
            </button>
            {userOpen && (
              <div className="absolute top-[calc(100%+6px)] right-0 min-w-[180px] bg-bg3 border border-border2 rounded p-1 z-[1000] shadow-card animate-fadeIn">
                <div className="px-3 py-2 border-b border-border mb-1">
                  <div className="text-[12px] font-semibold text-text truncate">{user.name}</div>
                  <div className="text-[10px] text-text3 truncate">{user.email}</div>
                </div>
                <button
                  onClick={() => {
                    setUserOpen(false);
                    navigate("/profile");
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 rounded-sm text-[12.5px] text-text2 hover:bg-white/5 hover:text-text transition-colors"
                >
                  <UserRound className="h-3.5 w-3.5" /> {t("nav.profile")}
                </button>
                <button
                  onClick={() => {
                    logout();
                    navigate("/login");
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 rounded-sm text-[12.5px] text-danger hover:bg-white/5 transition-colors"
                >
                  <LogOut className="h-3.5 w-3.5" /> {t("common.logout")}
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => navigate("/login")}
            className="flex items-center gap-1.5 h-9 px-2.5 rounded-sm bg-bg3 border border-border text-[11px] font-semibold text-text2 hover:border-primary hover:text-primary transition-all"
          >
            <LogIn className="h-3 w-3" /> {t("common.login")}
          </button>
        )}
      </div>
    </header>
  );
}
